import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const MarketingMessageAnalyzerViewResult = ({ result }) => {
  const getQualityColor = (score) => {
    if (!score && score !== 0) return 'bg-base-200 text-base-content/70';
    if (score >= 0.8) return 'bg-success/20 text-success';
    if (score >= 0.6) return 'bg-warning/20 text-warning';
    return 'bg-error/20 text-error';
  };

  const messageAnalysis = result?.message_analysis || {};
  const metadata = result?.metadata || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Value Proposition Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartPie size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Value Proposition Analysis</h3>
              </div>

              <div className="space-y-3">
                {/* Metrics */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span>Clarity</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(messageAnalysis?.message_elements?.value_proposition?.clarity)}`}>
                        {Math.round(messageAnalysis?.message_elements?.value_proposition?.clarity * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Uniqueness</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(messageAnalysis?.message_elements?.value_proposition?.uniqueness)}`}>
                        {Math.round(messageAnalysis?.message_elements?.value_proposition?.uniqueness * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Supporting Evidence */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Supporting Evidence</h4>
                  <div className="space-y-1">
                    {messageAnalysis?.message_elements?.value_proposition?.evidence?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <PhosphorIcons.Check className="text-success" size={14} />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.MegaphoneSimple size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Call to Action Analysis</h3>
              </div>

              <div className="p-3 bg-base-200 rounded-lg">
                <div className="space-y-2">
                  {Object.entries(messageAnalysis?.message_elements?.call_to_action || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="capitalize">{key}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(value)}`}>
                        {Math.round(value * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Brand Alignment Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-accent/10">
                  <PhosphorIcons.Target size={20} weight="bold" className="text-accent"/>
                </div>
                <h3 className="card-title text-lg">Brand Alignment</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span>Overall Score</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(messageAnalysis?.brand_alignment?.score)}`}>
                      {Math.round(messageAnalysis?.brand_alignment?.score * 100)}%
                    </span>
                  </div>
                </div>

                {messageAnalysis?.brand_alignment?.issues?.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <h4 className="font-medium mb-2">Issues</h4>
                    <div className="space-y-1">
                      {messageAnalysis.brand_alignment.issues.map((issue, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <PhosphorIcons.Warning className="text-warning" size={14} />
                          <span className="capitalize">{issue.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {messageAnalysis?.brand_alignment?.suggestions?.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <h4 className="font-medium mb-2">Suggestions</h4>
                    <div className="space-y-1">
                      {messageAnalysis.brand_alignment.suggestions.map((suggestion, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <PhosphorIcons.LightbulbFilament className="text-info" size={14} />
                          <span className="capitalize">{suggestion.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Audience Fit Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Users size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Audience Fit</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span>Fit Score</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(messageAnalysis?.audience_fit?.score)}`}>
                      {Math.round(messageAnalysis?.audience_fit?.score * 100)}%
                    </span>
                  </div>
                </div>

                {messageAnalysis?.audience_fit?.gaps?.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <h4 className="font-medium mb-2">Identified Gaps</h4>
                    <div className="space-y-1">
                      {messageAnalysis.audience_fit.gaps.map((gap, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <PhosphorIcons.Info className="text-info" size={14} />
                          <span className="capitalize">{gap.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingMessageAnalyzerViewResult;